import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Check,
  ArrowRight,
  TrendingUp,
  Shield,
  Users,
  BarChart3,
  FileText,
  Clock,
  Zap,
  Star,
  Sparkles,
  ChevronDown,
  Camera,
  Lock,
  Wallet,
  PieChart,
  CheckCircle2,
  AlertCircle,
  Mail,
  Phone,
  User
} from 'lucide-react';

function LandingPage({ onNavigateToLogin, onNavigateToRegister, onNavigateToRegisterEmployee }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('revenue');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const features = [
    {
      icon: TrendingUp,
      title: 'Gestion Revenus Intelligente',
      description: 'Caisses, CHIFA, virements - tout centralisé avec calcul automatique des écarts',
      gradient: 'from-[#004d3d] to-[#00ff7f]',
    },
    {
      icon: Camera,
      title: 'Upload Factures',
      description: 'Prenez en photo vos factures directement depuis votre mobile',
      gradient: 'from-[#B5B3E5] to-[#F5876A]',
    },
    {
      icon: BarChart3,
      title: 'Clôtures Automatiques',
      description: 'Rapports hebdomadaires et mensuels générés automatiquement',
      gradient: 'from-[#FFDA60] to-[#F5876A]',
    },
    {
      icon: Users,
      title: 'Multi-Utilisateurs',
      description: 'Gérez votre équipe avec des rôles : Admin, Caissier, Comptable',
      gradient: 'from-[#00ff7f] to-[#80D2EB]',
    },
    {
      icon: Shield,
      title: 'Sécurisé & Conforme',
      description: 'Données cryptées, sauvegardes automatiques, conformité fiscale algérienne',
      gradient: 'from-[#80D2EB] to-[#B5B3E5]',
    },
    {
      icon: Clock,
      title: 'Gain de Temps',
      description: 'Automatisez vos tâches comptables et concentrez-vous sur vos patients',
      gradient: 'from-[#F5876A] to-[#FFDA60]',
    },
  ];

  const plans = [
    {
      name: 'Starter',
      subtitle: 'Découvrez MIZAN gratuitement',
      price: 'Gratuit',
      period: '30 jours',
      badge: null,
      popular: false,
      features: [
        '1 utilisateur (pharmacien)',
        'Gestion revenus/dépenses basique',
        '1 caisse',
        'Catégories pré-configurées',
        'Support email',
        '100 transactions/mois',
      ],
      cta: 'Essayer gratuitement',
      gradient: 'from-slate-50 to-slate-100',
    },
    {
      name: 'Professionnel',
      subtitle: 'Pour pharmacies actives',
      price: '4,500',
      period: 'DZD/mois',
      badge: null,
      popular: false,
      features: [
        '3 utilisateurs',
        'Gestion complète revenus/dépenses',
        'Caisses illimitées',
        'Upload factures illimité',
        'Clôtures automatiques',
        'Support email prioritaire',
        'Sauvegarde cloud automatique',
      ],
      cta: 'Choisir ce plan',
      gradient: 'from-green-50 to-teal-50',
    },
    {
      name: 'Premium',
      subtitle: 'Recommandé pour pharmacies',
      price: '9,000',
      period: 'DZD/mois',
      badge: '⭐ Le plus populaire',
      popular: true,
      features: [
        '10 utilisateurs',
        'Toutes les fonctionnalités Pro',
        'Rapports avancés & KPIs',
        'Prévisions de trésorerie',
        'API access',
        'Support téléphonique prioritaire',
        'Formation personnalisée',
        'Manager de compte dédié',
      ],
      cta: 'Choisir Premium',
      gradient: 'from-green-100 to-teal-100',
    },
    {
      name: 'Entreprise',
      subtitle: 'Pour groupes de pharmacies',
      price: 'Sur devis',
      period: 'Personnalisé',
      badge: null,
      popular: false,
      features: [
        'Utilisateurs illimités',
        'Multi-pharmacies',
        'Intégration ERP sur mesure',
        'Conformité avancée',
        'SLA garanti 99.9%',
        'Support 24/7',
        'Serveur dédié (option)',
        'Développement de fonctionnalités',
      ],
      cta: 'Nous contacter',
      gradient: 'from-slate-50 to-teal-50',
    },
  ];

  const faqs = [
    {
      question: 'Puis-je essayer gratuitement avant de m\'engager ?',
      answer:
        'Oui ! Tous nos forfaits incluent 30 jours d\'essai gratuit, sans carte bancaire requise. Vous pouvez tester toutes les fonctionnalités en toute liberté.',
    },
    {
      question: 'Mes données sont-elles sécurisées ?',
      answer:
        'Absolument. Vos données sont cryptées (AES-256), sauvegardées quotidiennement et hébergées en Algérie. Nous sommes conformes aux normes de sécurité bancaire.',
    },
    {
      question: 'MIZAN est-il conforme aux normes comptables algériennes ?',
      answer:
        'Oui, MIZAN est conçu pour respecter les normes fiscales et comptables algériennes. Toutes les catégories et rapports sont adaptés à la législation locale.',
    },
    {
      question: 'Puis-je importer mes données depuis Excel ?',
      answer:
        'Oui, nous proposons un outil d\'import pour migrer facilement vos données existantes depuis Excel ou d\'autres logiciels comptables.',
    },
    {
      question: 'Quel support est disponible ?',
      answer:
        'Selon votre forfait : support email (Starter/Pro), support téléphonique prioritaire (Premium), ou support 24/7 avec manager dédié (Entreprise).',
    },
  ];

  const testimonials = [
    {
      name: 'Dr. Amina K.',
      role: 'Pharmacienne, Alger',
      content: 'Depuis MIZAN, je gagne 2 heures par jour. Les clôtures automatiques sont un vrai gain de temps !',
      rating: 5,
    },
    {
      name: 'Karim B.',
      role: 'Gérant, Oran',
      content: 'Fini les erreurs de caisse ! Le système de validation est parfait pour gérer mon équipe.',
      rating: 5,
    },
    {
      name: 'Sarah M.',
      role: 'Pharmacienne, Constantine',
      content: 'Interface intuitive et support réactif. Je recommande vivement pour toutes les pharmacies.',
      rating: 5,
    },
  ];

  const kpiCards = [
    { label: 'CA Mensuel', value: '2,450,000', unit: 'DZD', icon: TrendingUp, color: 'text-emerald-600' },
    { label: 'Bénéfice Net', value: '380,000', unit: 'DZD', icon: Wallet, color: 'text-green-600' },
    { label: 'Transactions', value: '1,247', unit: 'ce mois', icon: BarChart3, color: 'text-blue-600' },
    { label: 'Écarts Caisses', value: '0', unit: 'DZD', icon: CheckCircle2, color: 'text-teal-600' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
      {/* Navigation Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/90 backdrop-blur-lg shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#004d3d] to-[#00ff7f] flex items-center justify-center shadow-lg shadow-green-500/30">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-[#004d3d] to-[#00ff7f] bg-clip-text text-transparent">
              MIZAN
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={onNavigateToLogin}
              className="text-slate-700 hover:text-green-500 hover:bg-green-50"
            >
              Connexion
            </Button>
            <Button
              onClick={onNavigateToRegister}
              className="bg-gradient-to-r from-[#004d3d] to-[#00ff7f] hover:from-green-600 hover:to-teal-500 text-white shadow-lg shadow-green-500/30"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Essai gratuit
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Hero Section avec Dashboard 3D */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-96 h-96 bg-green-300/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-teal-300/30 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text Content */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="space-y-8"
            >
              <motion.div variants={fadeInUp}>
                <Badge className="bg-green-100 text-green-700 border-green-200 mb-4 px-4 py-1.5">
                  <Sparkles className="w-3 h-3 mr-1.5" />
                  500+ pharmacies nous font confiance
                </Badge>
              </motion.div>

              <motion.h1
                variants={fadeInUp}
                className="text-5xl lg:text-7xl font-bold leading-tight"
              >
                <span className="text-slate-900">LA COMPTABILITÉ</span>
                <br />
                <span className="bg-gradient-to-r from-[#004d3d] to-[#00ff7f] bg-clip-text text-transparent">
                  INTELLIGENTE
                </span>
                <br />
                <span className="text-slate-900">POUR VOTRE PHARMACIE</span>
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-xl text-slate-600 leading-relaxed max-w-xl"
              >
                Gérez vos finances en <span className="font-semibold text-green-500">10 minutes par jour</span>.
                Automatisez vos clôtures, éliminez les erreurs de caisse, et pilotez votre pharmacie en temps réel.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={onNavigateToLogin}
                  className="bg-gradient-to-r from-[#004d3d] to-[#00ff7f] hover:from-green-600 hover:to-teal-500 text-white shadow-2xl shadow-green-500/40 text-lg px-10 py-7"
                >
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Se Connecter
                </Button>
              </motion.div>

              <motion.div variants={fadeInUp} className="text-center mt-6">
                <p className="text-sm text-slate-600">
                  Nouveau ? Inscrivez votre pharmacie directement depuis la page de connexion
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex items-center gap-6 text-sm text-slate-500 justify-center">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Aucune carte bancaire</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>30 jours gratuits</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Annulation facile</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: 3D Dashboard Mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotateY: -15 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="relative"
            >
              <div className="relative transform perspective-1000" style={{ transform: 'rotateY(-5deg) rotateX(5deg)' }}>
                {/* Main Dashboard Card */}
                <Card className="bg-white/80 backdrop-blur-xl border-2 border-slate-200/50 shadow-2xl overflow-hidden">
                  <CardContent className="p-6 space-y-6">
                    {/* Header Tabs */}
                    <div className="flex gap-2 pb-4 border-b">
                      <button
                        onClick={() => setActiveTab('revenue')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          activeTab === 'revenue'
                            ? 'bg-green-100 text-green-700'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        Revenus
                      </button>
                      <button
                        onClick={() => setActiveTab('expense')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          activeTab === 'expense'
                            ? 'bg-green-100 text-green-700'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        Dépenses
                      </button>
                      <button
                        onClick={() => setActiveTab('closure')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          activeTab === 'closure'
                            ? 'bg-green-100 text-green-700'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        Clôtures
                      </button>
                    </div>

                    {/* KPI Cards Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      {kpiCards.map((kpi, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="bg-gradient-to-br from-slate-50 to-white p-4 rounded-xl border border-slate-200/50"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className="text-xs text-slate-500 font-medium">{kpi.label}</span>
                            <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                          </div>
                          <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
                          <div className="text-xs text-slate-400 mt-1">{kpi.unit}</div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Chart Placeholder */}
                    <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 border border-green-200/50">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-semibold text-slate-700">Évolution Revenus</span>
                        <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          +12.5%
                        </Badge>
                      </div>
                      {/* Simulated Chart */}
                      <div className="flex items-end gap-2 h-32">
                        {[65, 45, 80, 55, 90, 70, 95].map((height, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ height: 0 }}
                            animate={{ height: `${height}%` }}
                            transition={{ delay: 0.5 + idx * 0.1, duration: 0.5 }}
                            className="flex-1 bg-gradient-to-t from-[#004d3d] to-[#00ff7f] rounded-t-lg shadow-lg shadow-green-500/20"
                          />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Floating Badges */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 3 }}
                  className="absolute -top-6 -left-6 bg-white rounded-xl shadow-xl p-3 border border-slate-200"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="text-sm font-semibold text-slate-700">100% Précis</span>
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ repeat: Infinity, duration: 3, delay: 0.5 }}
                  className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-3 border border-slate-200"
                >
                  <div className="flex items-center gap-2">
                    <Lock className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-slate-700">Sécurisé</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="w-6 h-6 text-slate-400" />
        </motion.div>
      </section>

      {/* Social Proof Section */}
      <section className="py-12 bg-white/50 backdrop-blur-sm border-y border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-teal-400 border-2 border-white flex items-center justify-center text-white font-semibold text-sm shadow-sm"
                  >
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-slate-600">
                  <span className="font-bold text-slate-900">500+</span> pharmacies satisfaites
                </p>
              </div>
            </div>

            <div className="text-center md:text-left">
              <p className="text-lg italic text-slate-700 max-w-2xl">
                "Depuis MIZAN, je gagne <span className="font-bold text-green-500">2 heures par jour</span>. Les
                clôtures automatiques sont un vrai game-changer !"
              </p>
              <p className="text-sm text-slate-500 mt-2">— Dr. Amina K., Pharmacienne à Alger</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Bento Grid */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <Badge className="bg-green-100 text-green-700 border-green-200 mb-4 px-4 py-1.5">
                <Sparkles className="w-3 h-3 mr-1.5" />
                Fonctionnalités Premium
              </Badge>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Tout ce dont vous avez besoin
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-slate-600 max-w-2xl mx-auto">
              Une plateforme complète pour gérer tous les aspects financiers de votre pharmacie
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {features.map((feature, idx) => (
              <motion.div key={idx} variants={fadeInUp}>
                <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-slate-200/50 bg-white/80 backdrop-blur-sm">
                  <CardContent className="p-6 space-y-4">
                    <div
                      className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center shadow-lg`}
                    >
                      <feature.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">{feature.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Dashboard Preview Section - LE COUP DE MAÎTRE */}
      <section className="py-24 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 text-white relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-400 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <Badge className="bg-green-500/20 text-green-300 border-green-400/30 mb-4 px-4 py-1.5">
                <PieChart className="w-3 h-3 mr-1.5" />
                Interface Intuitive
              </Badge>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold mb-4">
              Visualisez votre pharmacie en un coup d'œil
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-slate-300 max-w-2xl mx-auto">
              Un dashboard moderne et épuré qui vous donne toutes les informations essentielles en temps réel
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Screenshot Dashboard */}
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border-4 border-slate-700/50">
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 flex items-center gap-3">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="flex-1 text-center text-sm text-slate-400 font-mono">app.thikaaccounting.dz/dashboard</div>
              </div>
              <div className="p-8 bg-slate-50">
                {/* Tabs */}
                <div className="flex gap-2 mb-6">
                  <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium shadow-sm">Revenus</div>
                  <div className="px-4 py-2 bg-white text-slate-600 rounded-lg text-sm font-medium">Dépenses</div>
                  <div className="px-4 py-2 bg-white text-slate-600 rounded-lg text-sm font-medium">Clôtures</div>
                  <div className="px-4 py-2 bg-white text-slate-600 rounded-lg text-sm font-medium">Rapports</div>
                </div>

                {/* KPIs Grid */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {kpiCards.map((kpi, idx) => (
                    <div key={idx} className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs text-slate-500 font-medium">{kpi.label}</span>
                        <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                      </div>
                      <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
                      <div className="text-xs text-slate-400 mt-1">{kpi.unit}</div>
                    </div>
                  ))}
                </div>

                {/* Charts Section */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Revenue Chart */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-700">Évolution Revenus</h3>
                      <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        +12.5%
                      </Badge>
                    </div>
                    <div className="flex items-end gap-2 h-40">
                      {[65, 45, 80, 55, 90, 70, 95, 85, 100, 75, 90, 80].map((height, idx) => (
                        <div
                          key={idx}
                          style={{ height: `${height}%` }}
                          className="flex-1 bg-gradient-to-t from-[#004d3d] to-[#00ff7f] rounded-t-lg shadow-sm"
                        />
                      ))}
                    </div>
                    <div className="flex justify-between mt-3 text-xs text-slate-400">
                      <span>Jan</span>
                      <span>Juin</span>
                      <span>Déc</span>
                    </div>
                  </div>

                  {/* Expenses Chart */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-700">Répartition Dépenses</h3>
                      <Badge variant="outline" className="text-xs text-slate-600 border-slate-300">
                        Ce mois
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {[
                        { label: 'Salaires', percent: 45, color: 'bg-blue-500' },
                        { label: 'Fournisseurs', percent: 30, color: 'bg-purple-500' },
                        { label: 'Loyer', percent: 15, color: 'bg-orange-500' },
                        { label: 'Autres', percent: 10, color: 'bg-slate-400' },
                      ].map((item, idx) => (
                        <div key={idx}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-slate-600">{item.label}</span>
                            <span className="font-semibold text-slate-700">{item.percent}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              style={{ width: `${item.percent}%` }}
                              className={`h-full ${item.color} rounded-full`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
              className="absolute -top-8 -left-8 bg-white rounded-xl shadow-2xl p-4 border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#004d3d] to-[#00ff7f] flex items-center justify-center shadow-lg shadow-green-500/30">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Temps réel</p>
                  <p className="text-xs text-slate-500">Données synchronisées</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, 15, 0] }}
              transition={{ repeat: Infinity, duration: 4, delay: 1 }}
              className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-2xl p-4 border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Ultra rapide</p>
                  <p className="text-xs text-slate-500">Interface fluide</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center mt-12"
          >
            <Button
              size="lg"
              onClick={onNavigateToLogin}
              className="bg-white text-green-600 hover:bg-slate-100 shadow-2xl text-lg px-8 py-6"
            >
              <ArrowRight className="w-5 h-5 mr-2" />
              Essayer la démo interactive
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section - 4 Plans */}
      <section className="py-24 px-6 bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <Badge className="bg-green-100 text-green-700 border-green-200 mb-4 px-4 py-1.5">
                <Sparkles className="w-3 h-3 mr-1.5" />
                Tarifs Transparents
              </Badge>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Choisissez votre forfait
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-slate-600 max-w-2xl mx-auto">
              Tous les forfaits incluent 30 jours d'essai gratuit. Aucune carte bancaire requise.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {plans.map((plan, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className={`relative ${plan.popular ? 'lg:-mt-4 lg:mb-0' : ''}`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-[#004d3d] to-[#00ff7f] text-white border-0 shadow-lg shadow-green-500/30 px-4 py-1.5">
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                <Card
                  className={`h-full border-2 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${
                    plan.popular
                      ? 'border-green-500 shadow-xl shadow-green-500/20 bg-gradient-to-br from-green-50 to-teal-50'
                      : 'border-slate-200 bg-white/80 backdrop-blur-sm'
                  }`}
                >
                  <CardContent className="p-6 space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
                      <p className="text-sm text-slate-500 mt-1">{plan.subtitle}</p>
                    </div>

                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-5xl font-bold text-slate-900">{plan.price}</span>
                        {plan.price !== 'Gratuit' && plan.price !== 'Sur devis' && (
                          <span className="text-slate-500">DZD</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{plan.period}</p>
                    </div>

                    <Button
                      onClick={onNavigateToLogin}
                      className={`w-full ${
                        plan.popular
                          ? 'bg-gradient-to-r from-[#004d3d] to-[#00ff7f] hover:from-green-600 hover:to-teal-500 text-white shadow-lg shadow-green-500/30'
                          : 'bg-slate-900 hover:bg-slate-800 text-white'
                      }`}
                    >
                      {plan.cta}
                    </Button>

                    <div className="space-y-3 pt-6 border-t border-slate-200">
                      {plan.features.map((feature, featureIdx) => (
                        <div key={featureIdx} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center mt-12"
          >
            <p className="text-slate-600">
              Besoin d'un forfait personnalisé ?{' '}
              <button className="text-green-500 font-semibold hover:underline">Contactez-nous</button>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <Badge className="bg-green-100 text-green-700 border-green-200 mb-4 px-4 py-1.5">
                <Star className="w-3 h-3 mr-1.5" />
                Témoignages
              </Badge>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Ils nous font confiance
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, idx) => (
              <motion.div key={idx} variants={fadeInUp}>
                <Card className="h-full border-2 border-slate-200 bg-white hover:shadow-xl transition-all">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex gap-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-slate-700 italic leading-relaxed">"{testimonial.content}"</p>
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <Badge className="bg-green-100 text-green-700 border-green-200 mb-4 px-4 py-1.5">
                <AlertCircle className="w-3 h-3 mr-1.5" />
                Questions Fréquentes
              </Badge>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold text-slate-900">
              Vos questions, nos réponses
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="space-y-4"
          >
            {faqs.map((faq, idx) => (
              <motion.div key={idx} variants={fadeInUp}>
                <Card className="border-2 border-slate-200 bg-white hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-3">{faq.question}</h3>
                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-green-500 via-teal-400 to-cyan-400 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-4">Prêt à révolutionner votre comptabilité ?</h2>
              <p className="text-xl text-white/90">
                Rejoignez 500+ pharmaciens qui gagnent du temps chaque jour avec MIZAN.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                onClick={onNavigateToLogin}
                className="bg-white text-green-600 hover:bg-slate-100 shadow-2xl text-lg px-10 py-6"
              >
                <Zap className="w-5 h-5 mr-2" />
                Démarrer l'essai gratuit - 30 jours
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-white/90">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                <span>Aucune carte bancaire requise</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                <span>Installation en 5 minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                <span>Annulation à tout moment</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#004d3d] to-[#00ff7f] flex items-center justify-center shadow-lg shadow-green-500/20">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">MIZAN</span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                La solution comptable moderne conçue spécialement pour les pharmacies algériennes.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-bold mb-4">Produit</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <a href="#" className="hover:text-green-400 transition-colors">
                    Fonctionnalités
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-green-400 transition-colors">
                    Tarifs
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-green-400 transition-colors">
                    Sécurité
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-green-400 transition-colors">
                    Mises à jour
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <a href="#" className="hover:text-green-400 transition-colors">
                    Documentation
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-green-400 transition-colors">
                    Guides
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-green-400 transition-colors">
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-green-400 transition-colors">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:contact@thikaaccounting.dz" className="hover:text-green-400 transition-colors">
                    contact@thikaaccounting.dz
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+213 XX XX XX XX</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-400">
            <p>&copy; 2025 MIZAN. Tous droits réservés.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-green-400 transition-colors">
                Mentions légales
              </a>
              <a href="#" className="hover:text-green-400 transition-colors">
                Politique de confidentialité
              </a>
              <a href="#" className="hover:text-green-400 transition-colors">
                CGU
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
